import { yupResolver } from '@hookform/resolvers/yup';
import { FieldErrors, Resolver } from 'react-hook-form';
import * as Yup from 'yup';

import { EditTagFormValues } from './types/formValues';
import {
  isVisibleAdvancedSection,
  isVisibleCategoryColumn,
  isVisibleMaxDecimal,
  isVisibleMinMaxValue,
  isVisibleRelevantMode,
} from './editTagModalFormV2Rules';

export type ValidationOptions = {
  hasAttemptedSubmit: boolean;
  disableControllerFields: boolean;
};

type SchemaContext = ValidationOptions & { form: EditTagFormValues };
type ErrorCategory = 'required' | 'nonNumeric' | 'duplicateValue' | 'individual';

const MIN_VALUE_INVALID = 'Minimum value must be less than maximum value!';
const MAX_VALUE_INVALID = 'Maximum value must be greater than minimum value!';
const MINUS_SIGN_ISSUE = 'Value should be a number!';
const NON_UNIQUE_VALUE_ISSUE = 'Values should be unique!';
const NAME_REQUIRED_ISSUE = 'Name is required!';
const VALUE_REQUIRED_ISSUE = 'Value is required!';
const MIN_VALUE_REQUIRED_ISSUE = 'Minimum value is required!';
const MAX_VALUE_REQUIRED_ISSUE = 'Maximum value is required!';
const DATA_TYPE_REQUIRED_ISSUE = 'Data Type is required!';
const CONTROLLABLE_REQUIRED_ISSUE = 'Controllable is required!';
const ALLOWED_INPUTS_REQUIRED_ISSUE = 'Allowed Inputs is required!';
const CONTROLLER_REQUIRED_ISSUE = 'Controller is required!';
const RELEVANT_MODE_REQUIRED_ISSUE = 'Relevant Mode is required!';
const MAX_DECIMAL_REQUIRED_ISSUE = 'Maximum decimal is required!';
const MANDATORY_FIELDS_BANNER_MESSAGE = 'Please enter all mandatory fields';

export const NO_CONTROLLERS_HELPER_TEXT =
  'To enable control, a Controller must be added under Settings/Controllers.';

const isMinMaxRangeInvalid = (form: Pick<EditTagFormValues, 'minValue' | 'maxValue'>): boolean =>
  form.minValue !== '-' &&
  form.maxValue !== '-' &&
  form.minValue !== '' &&
  form.maxValue !== '' &&
  Number(form.minValue) >= Number(form.maxValue);

// Test names are `<field>-<category>`; yup defaults a failing test's error `type`
// to its own name, and RHF carries that onto `FieldError.type`, so the category
// suffix is how the banner groups errors without a separate side channel.
function fail(this: Yup.TestContext, message: string) {
  return this.createError({ message });
}

export const editTagModalFormV2Schema = Yup.object().shape({
  dataTypeOfValues: Yup.string().test('dataTypeOfValues-required', function (value) {
    const { hasAttemptedSubmit } = this.options.context as SchemaContext;
    if (hasAttemptedSubmit && value === '') return fail.call(this, DATA_TYPE_REQUIRED_ISSUE);
    return true;
  }),

  controllable: Yup.string().test('controllable-required', function (value) {
    const { form, hasAttemptedSubmit } = this.options.context as SchemaContext;
    if (!isVisibleAdvancedSection(form)) return true;
    if (hasAttemptedSubmit && value === '') return fail.call(this, CONTROLLABLE_REQUIRED_ISSUE);
    return true;
  }),

  allowedInputs: Yup.string().test('allowedInputs-required', function (value) {
    const { form, hasAttemptedSubmit } = this.options.context as SchemaContext;
    if (!isVisibleAdvancedSection(form)) return true;
    if (hasAttemptedSubmit && value === '') return fail.call(this, ALLOWED_INPUTS_REQUIRED_ISSUE);
    return true;
  }),

  controllerId: Yup.string().test('controllerId-required', function (value) {
    const { hasAttemptedSubmit, disableControllerFields } = this.options.context as SchemaContext;
    if (disableControllerFields) return true;
    if (hasAttemptedSubmit && value === '') return fail.call(this, CONTROLLER_REQUIRED_ISSUE);
    return true;
  }),

  relevantMode: Yup.array().test('relevantMode-required', function (value) {
    const { form, hasAttemptedSubmit } = this.options.context as SchemaContext;
    if (!isVisibleRelevantMode(form)) return true;
    if (hasAttemptedSubmit && (value?.length ?? 0) === 0) {
      return fail.call(this, RELEVANT_MODE_REQUIRED_ISSUE);
    }
    return true;
  }),

  maxDecimal: Yup.string().test('maxDecimal-required', function (value) {
    const { form, hasAttemptedSubmit } = this.options.context as SchemaContext;
    if (!isVisibleMaxDecimal(form)) return true;
    if (hasAttemptedSubmit && value === '') return fail.call(this, MAX_DECIMAL_REQUIRED_ISSUE);
    return true;
  }),

  minValue: Yup.string()
    .test('minValue-required', function (value) {
      const { form, hasAttemptedSubmit } = this.options.context as SchemaContext;
      if (!isVisibleMinMaxValue(form) || !hasAttemptedSubmit || value !== '') return true;
      return fail.call(this, MIN_VALUE_REQUIRED_ISSUE);
    })
    .test('minValue-nonNumeric', function (value) {
      const { form } = this.options.context as SchemaContext;
      if (!isVisibleMinMaxValue(form) || value !== '-') return true;
      return fail.call(this, MINUS_SIGN_ISSUE);
    })
    .test('minValue-individual', function () {
      const { form } = this.options.context as SchemaContext;
      if (!isVisibleMinMaxValue(form) || !isMinMaxRangeInvalid(form)) return true;
      return fail.call(this, MIN_VALUE_INVALID);
    }),

  maxValue: Yup.string()
    .test('maxValue-required', function (value) {
      const { form, hasAttemptedSubmit } = this.options.context as SchemaContext;
      if (!isVisibleMinMaxValue(form) || !hasAttemptedSubmit || value !== '') return true;
      return fail.call(this, MAX_VALUE_REQUIRED_ISSUE);
    })
    .test('maxValue-nonNumeric', function (value) {
      const { form } = this.options.context as SchemaContext;
      if (!isVisibleMinMaxValue(form) || value !== '-') return true;
      return fail.call(this, MINUS_SIGN_ISSUE);
    })
    .test('maxValue-individual', function () {
      const { form } = this.options.context as SchemaContext;
      if (!isVisibleMinMaxValue(form) || !isMinMaxRangeInvalid(form)) return true;
      return fail.call(this, MAX_VALUE_INVALID);
    }),

  // Nested tests use `context.form`, not `this.parent`, to reach sibling rows
  // for the duplicate check — `this.parent` here would only be the one row.
  values: Yup.array().of(
    Yup.object().shape({
      value: Yup.string()
        .test('value-required', function (value) {
          const { form, hasAttemptedSubmit } = this.options.context as SchemaContext;
          if (isVisibleMinMaxValue(form) || !hasAttemptedSubmit || value !== '') return true;
          return fail.call(this, VALUE_REQUIRED_ISSUE);
        })
        .test('value-nonNumeric', function (value) {
          const { form } = this.options.context as SchemaContext;
          if (isVisibleMinMaxValue(form) || value !== '-') return true;
          return fail.call(this, MINUS_SIGN_ISSUE);
        })
        .test('value-duplicateValue', function (value) {
          const { form } = this.options.context as SchemaContext;
          if (isVisibleMinMaxValue(form) || !value) return true;
          const rowIndex = Number(/values\[(\d+)\]/.exec(this.path)?.[1] ?? -1);
          const isDuplicate = form.values.some(
            (row, index) => index !== rowIndex && row.value === value
          );
          return isDuplicate ? fail.call(this, NON_UNIQUE_VALUE_ISSUE) : true;
        }),
      name: Yup.string().test('name-required', function (value) {
        const { form, hasAttemptedSubmit } = this.options.context as SchemaContext;
        if (isVisibleMinMaxValue(form)) return true;
        if (hasAttemptedSubmit && value === '' && isVisibleCategoryColumn(form)) {
          return fail.call(this, NAME_REQUIRED_ISSUE);
        }
        return true;
      }),
    })
  ),
});

// yupResolver only forwards `context` as given; a nested `values[i]` test can't
// reach the whole form on its own, so this injects it before delegating.
export const editTagModalFormV2Resolver: Resolver<EditTagFormValues, ValidationOptions> = (
  values,
  context,
  options
) =>
  yupResolver(editTagModalFormV2Schema)(
    values,
    { ...(context as ValidationOptions), form: values },
    options
  );

const categoryFromType = (type: string | undefined): ErrorCategory => {
  const category = type?.split('-').pop();
  if (category === 'required' || category === 'nonNumeric' || category === 'duplicateValue') {
    return category;
  }
  return 'individual';
};

const BANNER_MESSAGE_BY_CATEGORY: Partial<Record<ErrorCategory, string>> = {
  required: MANDATORY_FIELDS_BANNER_MESSAGE,
  nonNumeric: MINUS_SIGN_ISSUE,
  duplicateValue: NON_UNIQUE_VALUE_ISSUE,
};

type FlatError = { message?: string; type?: string };

/** Walks `formState.errors` to any depth (flat fields and nested `values[i]`
 * rows alike) and collects every leaf error object. Uses `unknown` + a runtime
 * guard rather than `FieldErrors`'s own deeply-conditional types, which TS
 * can't cleanly narrow through `Array.isArray`/`Object.values`. */
const flattenFieldErrors = (errors: FieldErrors<EditTagFormValues>): FlatError[] => {
  const flat: FlatError[] = [];
  const collect = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;
    if (typeof (value as FlatError).type === 'string') {
      flat.push(value as FlatError);
      return;
    }
    Object.values(value as Record<string, unknown>).forEach(collect);
  };
  Object.values(errors).forEach(collect);
  return flat;
};

export const getErrorBannerMessages = (errors: FieldErrors<EditTagFormValues>): string[] => {
  const allErrors = flattenFieldErrors(errors).map((error) => ({
    message: error.message ?? '',
    category: categoryFromType(error.type),
  }));
  const hasRequiredError = allErrors.some((error) => error.category === 'required');
  const otherMessages = Array.from(
    new Set(
      allErrors
        .filter((error) => error.category !== 'required')
        .map((error) => BANNER_MESSAGE_BY_CATEGORY[error.category] ?? error.message)
    )
  );
  return hasRequiredError ? [MANDATORY_FIELDS_BANNER_MESSAGE, ...otherMessages] : otherMessages;
};
